import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { resolveShortLink, ResolveResult } from "api/short-link/short-link";

type ViewState =
  | { kind: "loading" }
  | { kind: "expired" }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

// Public route the rental company's renters land on after tapping a short
// payment URL from SMS. The renter's browser hits this React app first
// (because the URL is on the rental company's custom domain, which serves
// Template-1Now-FE). We immediately ask Django for the underlying Stripe /
// Square checkout URL and bounce the browser there via
// `window.location.replace` — no history entry, no chrome from the rest of
// the renter site. The renter sees a minimal "redirecting" splash for the
// network round-trip (~150–400 ms) and then Stripe.

const SHELL_STYLE: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "#0d0d10",
  color: "#f5f5f7",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  padding: "32px",
  textAlign: "center",
};

const TITLE_STYLE: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 500,
  letterSpacing: "0.02em",
};

const SUB_STYLE: React.CSSProperties = {
  marginTop: 12,
  fontSize: 13,
  color: "#9a9aa3",
};

const DOT_BASE: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: "#ff7448",
  display: "inline-block",
  margin: "0 4px",
  animation: "pmtdot 1s infinite ease-in-out",
};

const KEYFRAMES = `@keyframes pmtdot {
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}`;

const PaymentLinkRedirect = () => {
  // App.tsx early-returns this component for any /pay/* path, so it
  // renders OUTSIDE the React Router <Route> tree — useParams() would
  // return an empty object here. Parse the slug from the pathname
  // directly so this works whether we're rendered inside or outside a
  // matched route. useLocation is safe (it only requires being inside
  // <BrowserRouter>, which we are).
  const { pathname } = useLocation();
  const slug = useMemo(() => {
    const match = pathname.match(/^\/pay\/([^/?#]+)/);
    return match ? match[1] : undefined;
  }, [pathname]);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  // Replace the renter site's default tab title ("Car Rental" etc.) so the
  // few hundred milliseconds the splash is visible read as "the payment
  // page is loading," not as a random page in the rental site. We restore
  // the previous title on unmount so back-navigation doesn't get stuck on
  // this string.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Redirecting to payment…";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (!slug) {
      setState({ kind: "not_found" });
      return;
    }

    let cancelled = false;
    (async () => {
      const result: ResolveResult = await resolveShortLink(slug);
      if (cancelled) return;
      if (result.kind === "ok") {
        window.location.replace(result.targetUrl);
        return;
      }
      setState(result);
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    // Mirror the visible state in the tab title so the screen-reader /
    // tab-preview text matches what's on screen if the redirect fails.
    if (state.kind === "expired") document.title = "Payment link expired";
    else if (state.kind === "not_found") document.title = "Payment link not found";
    else if (state.kind === "error") document.title = "Payment unavailable";
  }, [state.kind]);

  return (
    <div style={SHELL_STYLE}>
      <style>{KEYFRAMES}</style>

      {state.kind === "loading" && (
        <>
          <div aria-hidden="true">
            <span style={DOT_BASE} />
            <span style={{ ...DOT_BASE, animationDelay: "0.15s" }} />
            <span style={{ ...DOT_BASE, animationDelay: "0.3s" }} />
          </div>
          <h2 style={{ ...TITLE_STYLE, marginTop: 24 }}>
            Redirecting to secure payment
          </h2>
          <p style={SUB_STYLE}>Please wait a moment.</p>
        </>
      )}

      {state.kind === "expired" && (
        <>
          <h2 style={TITLE_STYLE}>This payment link has expired</h2>
          <p style={SUB_STYLE}>
            Please contact the rental company for a fresh link.
          </p>
        </>
      )}

      {state.kind === "not_found" && (
        <>
          <h2 style={TITLE_STYLE}>Payment link not found</h2>
          <p style={SUB_STYLE}>
            The link may be mistyped or no longer active.
          </p>
        </>
      )}

      {state.kind === "error" && (
        <>
          <h2 style={TITLE_STYLE}>Couldn't load the payment page</h2>
          <p style={SUB_STYLE}>{state.message}</p>
        </>
      )}
    </div>
  );
};

export default PaymentLinkRedirect;
